import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { LayoutDashboard, FolderOpen, Users, BarChart3, Settings, LifeBuoy, ChevronDown, ChevronRight, Home, Building2, Activity, Check, Layers, Sparkles, MessageSquare, ExternalLink } from 'lucide-react';
import { useBuilder, buildersConfig } from '../../contexts/BuilderContext';
type UserRole = 'builder' | 'admin';
export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const {
    builder
  } = useBuilder();
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // Determine current role from URL
  const currentRole: UserRole = location.pathname.startsWith('/admin') ? 'admin' : 'builder';
  // Get the base path for builder routes
  const builderBasePath = builderId ? `/${builderId}` : '/mattamy';
  // Mock project count - in real app, this would come from API/context
  const projectCount = 2;
  // Auto-expand sections based on current route
  useEffect(() => {
    if (location.pathname.includes('/projects')) setProjectsOpen(true);
    if (location.pathname.includes('/options') || location.pathname.includes('/requests')) {
      setExpandedCategories(new Set(['options']));
    }
  }, [location.pathname]);
  const handleRoleSwitch = (role: UserRole, targetBuilderId?: string) => {
    setAccountMenuOpen(false);
    if (role === 'admin') {
      navigate('/admin/dashboard');
    } else {
      const slug = targetBuilderId || 'mattamy';
      navigate(`/${slug}/dashboard`);
    }
  };
  const NavItem = ({
    to,
    icon: Icon,
    label,
    exact = false,
    indent = false,
    badge
  }: any) => <NavLink to={to} end={exact} className={({
    isActive
  }) => `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-primary-500/10 text-primary-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'} ${indent ? 'ml-6' : ''}`} data-id="element-60">
      {Icon && <Icon className="mr-3 h-5 w-5 flex-shrink-0" data-id="element-61" />}
      <span className="flex-1" data-id="element-62">{label}</span>
      {badge && <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded" data-id="element-63">
          {badge}
        </span>}
    </NavLink>;
  // Get builder name for display
  const builderName = builder?.name || 'Unknown Builder';
  return <div className="flex h-full w-64 flex-col bg-slate-900" data-id="element-64">
      {/* Logo Area */}
      <div className="flex h-16 items-center px-6 border-b border-slate-800" data-id="element-65">
        <div className="flex items-center gap-2" data-id="element-66">
          <div className="bg-primary-500 p-1.5 rounded-lg" data-id="element-67">
            <Home className="h-5 w-5 text-white" strokeWidth={2.5} data-id="element-68" />
          </div>
          <span className="text-lg font-bold text-white tracking-tight" data-id="element-69">
            {currentRole === 'builder' ? builderName : 'ResVR Admin'}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto" data-id="element-70">
        {currentRole === 'builder' ? <>
            <NavItem to={`${builderBasePath}/dashboard`} icon={LayoutDashboard} label="Dashboard" data-id="element-71" />

            {/* Only show Projects if more than 1 project */}
            {projectCount > 1 && <NavItem to={`${builderBasePath}/projects`} icon={FolderOpen} label="Projects" data-id="element-72" />}

            {/* Options Group */}
            <div data-id="element-73">
              <button onClick={() => {
            const optionsOpen = expandedCategories.has('options');
            const next = new Set(expandedCategories);
            if (optionsOpen) {
              next.delete('options');
            } else {
              next.add('options');
            }
            setExpandedCategories(next);
          }} className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors" data-id="element-74">
                <div className="flex items-center" data-id="element-75">
                  <Layers className="mr-3 h-5 w-5" data-id="element-76" />
                  Options
                </div>
                {expandedCategories.has('options') ? <ChevronDown className="h-4 w-4" data-id="element-77" /> : <ChevronRight className="h-4 w-4" data-id="element-78" />}
              </button>

              {expandedCategories.has('options') && <div className="mt-1 space-y-1" data-id="element-79">
                  <NavItem to={`${builderBasePath}/options`} label="Manage Options" indent exact data-id="element-80" />
                  <NavItem to={`${builderBasePath}/requests`} label="Change Requests" indent data-id="element-81" />
                </div>}
            </div>

            {/* Customers - Single Link */}
            <NavItem to={`${builderBasePath}/customers`} icon={Users} label="Customers" data-id="element-82" />

            <NavItem to={`${builderBasePath}/analytics`} icon={BarChart3} label="Analytics" data-id="element-83" />
            <NavItem to={`${builderBasePath}/settings`} icon={Settings} label="Settings" data-id="element-84" />
            <NavItem to={`${builderBasePath}/support`} icon={LifeBuoy} label="Support" data-id="element-85" />
          </> : <>
            <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" data-id="element-86" />
            <NavItem to="/admin/builders" icon={Building2} label="Builders" data-id="element-87" />
            <NavItem to="/admin/projects" icon={FolderOpen} label="Projects" data-id="element-88" />
            <NavItem to="/admin/users" icon={Users} label="Users" data-id="element-89" />
            <NavItem to="/admin/requests" icon={MessageSquare} label="Requests" data-id="element-90" />
            <NavItem to="/admin/telemetry" icon={Activity} label="Telemetry" data-id="element-91" />
            <NavItem to="/admin/settings" icon={Settings} label="System Settings" data-id="element-92" />
          </>}
      </nav>

      {/* ResVR Logo - Subtle branding above user profile */}
      <div className="px-6 py-6" data-id="element-93">
        <img src="https://cdn.magicpatterns.com/uploads/8RmiUPQRfa1y1Vy5vUrsgN/resvr-logo-sidebar.png" alt="ResVR" className="h-10 w-auto opacity-40" data-id="element-94" />
      </div>

      {/* User Profile / Footer with Account Switcher */}
      <div className="border-t border-slate-800 p-4 relative" data-id="element-95">
        <button onClick={() => setAccountMenuOpen(!accountMenuOpen)} className="flex items-center gap-3 w-full hover:bg-slate-800 rounded-lg p-2 -m-2 transition-colors" data-id="element-96">
          <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 font-semibold text-sm flex-shrink-0" data-id="element-97">
            JD
          </div>
          <div className="flex-1 overflow-hidden text-left" data-id="element-98">
            <p className="truncate text-sm font-medium text-white" data-id="element-99">John Doe</p>
            <p className="truncate text-xs text-slate-400" data-id="element-100">
              {currentRole === 'admin' ? 'ResVR Admin' : 'Builder Admin'}
            </p>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`} data-id="element-101" />
        </button>

        {/* Account Switcher Dropdown */}
        {accountMenuOpen && <div className="absolute bottom-full left-4 right-4 mb-2 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-102">
            <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-103">
              {currentRole === 'admin' ? 'Switch Account' : 'Current Account'}
            </div>

            {/* Builder mode: Only show current builder */}
            {currentRole === 'builder' && builder && <>
                <div className="flex items-center justify-between w-full px-3 py-2 text-sm text-slate-300" data-id="element-104">
                  <div className="flex items-center gap-3" data-id="element-105">
                    <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center" data-id="element-106">
                      <Building2 className="h-4 w-4 text-slate-400" data-id="element-107" />
                    </div>
                    <div className="text-left" data-id="element-108">
                      <p className="text-sm font-medium text-white" data-id="element-109">
                        {builder.name}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-110">Builder Admin</p>
                    </div>
                  </div>
                  <Check className="h-4 w-4 text-primary-400" data-id="element-111" />
                </div>
                <div className="px-3 py-2 border-t border-slate-700 mt-2" data-id="element-112">
                  <p className="text-xs text-slate-500" data-id="element-113">
                    Contact your administrator to access other accounts.
                  </p>
                </div>
              </>}

            {/* Admin mode: Show all builders for easy switching */}
            {currentRole === 'admin' && <>
                {Object.values(buildersConfig).map(b => <button key={b.slug} onClick={() => handleRoleSwitch('builder', b.slug)} className="flex items-center justify-between w-full px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors" data-id="element-114">
                    <div className="flex items-center gap-3" data-id="element-115">
                      <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center" data-id="element-116">
                        <Building2 className="h-4 w-4 text-slate-400" data-id="element-117" />
                      </div>
                      <div className="text-left" data-id="element-118">
                        <p className="text-sm font-medium text-white" data-id="element-119">
                          {b.name}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-120">/{b.slug}</p>
                      </div>
                    </div>
                  </button>)}

                <div className="border-t border-slate-700 my-2" data-id="element-121" />

                {/* Current admin account */}
                <div className="flex items-center justify-between w-full px-3 py-2 text-sm text-slate-300" data-id="element-122">
                  <div className="flex items-center gap-3" data-id="element-123">
                    <div className="h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center" data-id="element-124">
                      <Home className="h-4 w-4 text-primary-400" data-id="element-125" />
                    </div>
                    <div className="text-left" data-id="element-126">
                      <p className="text-sm font-medium text-white" data-id="element-127">
                        ResVR Admin
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-128">Platform Admin</p>
                    </div>
                  </div>
                  <Check className="h-4 w-4 text-primary-400" data-id="element-129" />
                </div>
              </>}
          </div>}
      </div>
    </div>;
}